import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store, select } from '@ngrx/store';
import { of } from 'rxjs';
import { catchError, map, switchMap, tap, withLatestFrom } from 'rxjs/operators';

import { MatDialog } from '@angular/material/dialog';
import { getCurrentUserIsSystemUser } from 'src/app/authentication/state/authentication.selector';
import { OutgoingFaxDetailDialogComponent } from 'src/app/common-components/outgoing-fax-detail-dialog/outgoing-fax-detail-dialog.component';
import { FaxFilterOptions } from '../../models/fax-filter-option';
import { FaxOrderingOptions, FaxRegisterOptions } from '../../models/fax-register-options';
import { FaxRegisterListItem, FaxRegisterResults } from '../../models/fax-register-results.model';
import { GetFaxes, GetFaxes_faxesConnection_items } from '../../queries/generated/GetFaxes';
import { FaxRegisterApiService } from '../../services/fax-register-api.service';
import * as IncomingFaxResgisterActions from './incoming-fax-register.actions';
import { incomingFaxRegisterAdapterActions } from './incoming-fax-register.actions';
import { IncomingFaxRegisterState, selectEndCursor, selectQuickFilters } from './incoming-fax-register.reducer';
import * as FaxRegisterSelectors from './incoming-fax-register.selectors';
import { selectFilters, selectOrderingOptions } from './incoming-fax-register.selectors';
@Injectable()
export class IncomingFaxRegisterEffects {
   loadFaxList$ = createEffect(() => {
      return this.actions$.pipe(
         ofType(incomingFaxRegisterAdapterActions.pageEnter),
         withLatestFrom(
            this.store.pipe(select(selectFilters)),
            this.store.pipe(select(selectQuickFilters)),
            this.store.pipe(select(selectOrderingOptions))
         ),
         switchMap(([action, filters, quickFilters, orderingOptions]) => {
            return this.loadNewData(filters, quickFilters, orderingOptions);
         })
      );
   });

   loadFaxPage$ = createEffect(() => {
      return this.actions$.pipe(
         ofType(incomingFaxRegisterAdapterActions.loadPage),
         withLatestFrom(
            this.store.pipe(select(selectEndCursor)),
            this.store.pipe(select(selectQuickFilters)),
            this.store.pipe(select(selectFilters)),
            this.store.pipe(select(selectOrderingOptions))
         ),
         switchMap(([action, endCursor, quickFilters, filters, orderingOptions]) => {
            return this.faxRegisterApiService.getFaxes(endCursor, filters, quickFilters, orderingOptions).pipe(
               map((result) => {
                  return incomingFaxRegisterAdapterActions.loadPageSuccess(
                     this.convertGetFaxResultToLoadSuccessProps(result.data)
                  );
               }),
               catchError((error) => {
                  return of(incomingFaxRegisterAdapterActions.loadPageFailure(error));
               })
            );
         })
      );
   });

   quickFilterChanged$ = createEffect(() => {
      return this.actions$.pipe(
         ofType(incomingFaxRegisterAdapterActions.quickFilterChanged),
         withLatestFrom(
            this.store.pipe(select(selectQuickFilters)),
            this.store.pipe(select(FaxRegisterSelectors.selectFilters)),
            this.store.pipe(select(selectOrderingOptions))
         ),
         switchMap(([action, quickFilters, filters, orderingOptions]) => {
            return this.loadNewData(filters, quickFilters, orderingOptions);
         })
      );
   });

   orderingOptionsChanged$ = createEffect(() => {
      return this.actions$.pipe(
         ofType(incomingFaxRegisterAdapterActions.orderingOptionsChanged),
         withLatestFrom(
            this.store.pipe(select(selectFilters)),
            this.store.pipe(select(selectQuickFilters)),
            this.store.pipe(select(selectOrderingOptions))
         ),
         switchMap(([action, filters, quickfilters, orderingOptions]) => {
            return this.loadNewData(filters, quickfilters, orderingOptions);
         })
      );
   });

   openDialog$ = createEffect(
      () => {
         return this.actions$.pipe(
            ofType(IncomingFaxResgisterActions.openDialog),
            withLatestFrom(this.store.pipe(select(getCurrentUserIsSystemUser))),
            tap(([action, isSystemUser]) => {
               this.dialog.open(OutgoingFaxDetailDialogComponent, {
                  data: { faxLogs: action.faxLogs, isSystemUser },
                  width: '700px',
               });
            })
         );
      },
      { dispatch: false }
   );

   constructor(
      private actions$: Actions,
      private store: Store<IncomingFaxRegisterState>,
      private faxRegisterApiService: FaxRegisterApiService,
      private dialog: MatDialog
   ) {}

   private loadNewData(
      filters: FaxFilterOptions[],
      quickFilters: FaxRegisterOptions | null = null,
      orderingOptions: FaxOrderingOptions[] | null
   ) {
      return this.faxRegisterApiService.getFaxes(null, filters, quickFilters, orderingOptions).pipe(
         map((result) => {
            return incomingFaxRegisterAdapterActions.loadSuccess(
               this.convertGetFaxResultToLoadSuccessProps(result.data)
            );
         }),
         catchError((error) => {
            return of(incomingFaxRegisterAdapterActions.loadFailure(error));
         })
      );
   }

   private convertGetFaxResultToLoadSuccessProps(getFax: GetFaxes): FaxRegisterResults {
      return {
         endCursor: getFax.faxesConnection.pageInfo.endCursor,
         hasNextPage: getFax.faxesConnection.pageInfo.hasNextPage,
         items: getFax.faxesConnection.items.map(this.convertFaxRegisterListItem),
         totalCount: getFax.faxesConnection.totalCount,
      };
   }

   private convertFaxRegisterListItem(getFaxItem: GetFaxes_faxesConnection_items): FaxRegisterListItem {
      return {
         faxId: getFaxItem.faxId,
         extensionId: getFaxItem.extensionId,
         faxType: getFaxItem.faxTypeId,
         faxStatus: getFaxItem.faxStatusName,
         faxSource: getFaxItem.from,
         faxDestination: getFaxItem.to,
         processedTime: getFaxItem.processedTime,
         createDate: getFaxItem.createDate,
         updateDate: getFaxItem.updateDate,
         exceptionMessage: getFaxItem.exceptionMessage,
         sipCallId: getFaxItem.sipCallId,
         associatedProviderId: getFaxItem.associatedProviderId,
         associatedIp: getFaxItem.associatedIp,
         podIp: getFaxItem.podIp,
         podName: getFaxItem.podName,
         attachments: getFaxItem.attachmentsConnection ?? null,
      };
   }
}
